import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../../services/api";
import "./staff.css";

export default function StaffList() {
  const school    = JSON.parse(localStorage.getItem("schoolAdmin"));
  const navigate  = useNavigate();

  const [staff,       setStaff]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [roleFilter,  setRoleFilter]  = useState("");

  /* archive modal */
  const [archiveId,     setArchiveId]     = useState(null);
  const [relievingDate, setRelievingDate] = useState("");

  /* ── toggle app visibility ── */
  const handleToggleVisibility = async (id, current) => {
    try {
      const res = await API.patch(
        `/school-admin/staff/toggle-app-visibility/${id}/`,
        {},
        { headers: { "X-SCHOOL-ID": school.school.id } },
      );
      setStaff(prev => prev.map(s => s.id === id ? { ...s, show_in_app: res.data.show_in_app } : s));
    } catch (err) { console.error(err); alert("Failed to update visibility"); }
  };

  /* ── load ── */
  const loadStaff = async () => {
    try {
      setLoading(true);
      const res = await API.get("/school-admin/staff/", {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setStaff(res.data.staff || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadStaff(); }, []);

  /* ── archive ── */
  const handleArchive = async () => {
    if (!archiveId || !relievingDate) return alert("Relieving date is required");
    try {
      await API.post(
        `/school-admin/staff/archive/${archiveId}/`,
        { relieving_date: relievingDate },
        { headers: { "X-SCHOOL-ID": school.school.id } },
      );
      setArchiveId(null);
      setRelievingDate("");
      loadStaff();
    } catch (err) { console.error(err); alert("Error archiving staff"); }
  };

  /* ── delete ── */
  const handleDelete = async (id) => {
    if (!window.confirm("Permanently delete this staff member?")) return;
    try {
      await API.delete(`/school-admin/staff/delete/${id}/`, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      loadStaff();
    } catch (err) { console.error(err); alert("Failed to delete staff"); }
  };

  /* ── filter ── */
  const filtered = staff.filter(s => {
    const q = search.toLowerCase();
    return (
      (s.name?.toLowerCase().includes(q) || s.role?.toLowerCase().includes(q)) &&
      (roleFilter ? s.role === roleFilter : true)
    );
  });

  const uniqueRoles = [...new Set(staff.map(s => s.role).filter(Boolean))];

  const photoUrl = (p) => {
    if (!p) return null;
    return p.startsWith("http") ? p : `https://navith.cloud${p}`;
  };

  return (
    <div className="st-page">

      {/* ══ HERO ══ */}
      <div className="st-hero">
        <div className="st-hero-left">
          <div className="st-hero-icon">👥</div>
          <div>
            <h2 className="st-hero-title">Staff Members</h2>
            <p className="st-hero-sub">Manage all non-teaching staff — {staff.length} total</p>
          </div>
        </div>
        <div className="st-hero-right">
          <button className="st-btn-secondary" onClick={() => navigate("/school-admin/staff/archived")}>
            📦 Archived
          </button>
          <button className="st-btn-primary" onClick={() => navigate("/school-admin/staff/add")}>
            + Add Staff
          </button>
        </div>
      </div>

      {/* ══ FILTER ══ */}
      <div className="st-filter">
        <input
          className="st-search"
          placeholder="🔍  Search by name or role…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="st-select"
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
        >
          <option value="">All Roles</option>
          {uniqueRoles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {/* ══ TABLE ══ */}
      <div className="st-card">
        {loading ? (
          <div className="st-empty">
            <div className="st-empty-icon">⏳</div>
            <strong>Loading staff…</strong>
          </div>
        ) : filtered.length === 0 ? (
          <div className="st-empty">
            <div className="st-empty-icon">👤</div>
            <strong>No staff found</strong>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          <table className="st-table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Name &amp; Role</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Salary</th>
                <th>Joining Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id}>
                  <td>
                    {photoUrl(s.photo)
                      ? <img src={photoUrl(s.photo)} alt={s.name} className="st-avatar" />
                      : <div className="st-avatar-init">{s.name?.charAt(0).toUpperCase() || "S"}</div>
                    }
                  </td>
                  <td>
                    <div className="st-name">{s.name}</div>
                    <div className="st-role">{s.role}</div>
                  </td>
                  <td>{s.phone || "—"}</td>
                  <td>{s.email || "—"}</td>
                  <td style={{ fontWeight: 700 }}>₹ {Number(s.salary || 0).toLocaleString()}</td>
                  <td>{s.joining_date || "—"}</td>
                  <td>
                    <div className="st-actions">
                      <button
                        className="st-btn-edit"
                        onClick={() => navigate(`/school-admin/staff/edit/${s.id}`, { state: { staff: s } })}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleToggleVisibility(s.id, s.show_in_app)}
                        title={s.show_in_app ? "Visible in app — click to hide" : "Hidden from app — click to show"}
                        style={{
                          padding: "6px 14px",
                          borderRadius: "20px",
                          border: "none",
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: "12px",
                          background: s.show_in_app
                            ? "linear-gradient(135deg,#43e97b,#38f9d7)"
                            : "linear-gradient(135deg,#9ca3af,#6b7280)",
                          color: "#fff",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.show_in_app ? "👁 Visible" : "🙈 Hidden"}
                      </button>
                      <button
                        className="st-btn-archive"
                        onClick={() => setArchiveId(s.id)}
                      >
                        📦 Archive
                      </button>
                      <button
                        className="st-btn-delete"
                        onClick={() => handleDelete(s.id)}
                      >
                        🗑 Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ══ ARCHIVE MODAL ══ */}
      {archiveId && (
        <div className="st-modal-overlay" onClick={() => { setArchiveId(null); setRelievingDate(""); }}>
          <div className="st-modal" onClick={e => e.stopPropagation()}>
            <div className="st-modal-head">
              <span style={{ fontSize: "26px" }}>📦</span>
              <div>
                <h3>Archive Staff Member</h3>
                <p>This will move the staff to the archived list</p>
              </div>
            </div>
            <div className="st-modal-body">
              <label className="st-modal-note">Select Relieving Date</label>
              <input
                type="date"
                value={relievingDate}
                onChange={e => setRelievingDate(e.target.value)}
                style={{
                  width: "100%", padding: "11px 14px",
                  borderRadius: "10px", border: "1.5px solid #c4b5fd",
                  fontSize: "14px", color: "#1e293b", background: "#fafafa",
                  outline: "none", boxSizing: "border-box", marginTop: "8px",
                }}
              />
            </div>
            <div className="st-modal-actions">
              <button
                className="st-modal-cancel"
                onClick={() => { setArchiveId(null); setRelievingDate(""); }}
              >
                Cancel
              </button>
              <button className="st-modal-confirm" onClick={handleArchive}>
                ✓ Archive
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
